import {Chip, IconButton, Progress, Tooltip, Typography} from "@material-tailwind/react";
import {PencilIcon} from "@heroicons/react/24/solid";
import persianNumberFormat from "../../helper/PersianNumberFormat";
import React, {useContext, useState} from "react";
import {TableHead} from "../system/thead";
import {PaginateContext} from "../../context/paginate";
import {ServiceFollowUpContext} from "../../context/service_follow_up";

const getDeadlineStatus = (daysToDeadline) => {
  if (daysToDeadline === -1) {
    return {
      label: "مهلت امروز",
      rowClassName: "bg-amber-300",
      chipColor: "amber",
    };
  }

  if (daysToDeadline < -1) {
    return {
      label: "گذشته",
      rowClassName: "bg-red-300",
      chipColor: "red",
    };
  }

  return {
    label: "در مهلت",
    rowClassName: "bg-green-300",
    chipColor: "green",
  };
};

const formatDate = (value) => {
  if (!value) return "-";
  return persianNumberFormat(value.split(" ")[0]);
};

export function ServiceFollowUpGrid() {

  const {
    setSortBy,
    sortType,
    setSortType,
  } = useContext(PaginateContext)

  const {
    serviceFollowUps,
    setServiceFollowUp,
    setOpen,
    roleId,
  } = useContext(ServiceFollowUpContext);
  
  const TABLE_HEAD = roleId === 2
    ? ["#", "کاربر", "گرایش", "مرحله", "عنوان", "مهلت انجام", "پیشرفت", "عملیات"]
    : roleId === 3
      ? ["#", "مشاور", "گرایش", "مرحله", "عنوان", "مهلت انجام", "پیشرفت", "عملیات"]
      : ["#", "کاربر", "مشاور", "گرایش", "مرحله", "عنوان", "مهلت انجام", "پیشرفت", "عملیات"];

  const [selectedHead, setSelectedHead] = useState("#")

  const headMap = {
    "#": "",
    "کاربر": "person_name",
    "مشاور": "agent_name",
    "گرایش": "branch_name",
    "مرحله": "step",
    "عنوان": "step_title",
    "مهلت انجام": "dead_line",
    "پیشرفت": "progress",
    "عملیات": "",
  }

  const headSortable = {
    "#": false,
    "کاربر": true,
    "مشاور": true,
    "گرایش": true,
    "مرحله": true,
    "عنوان": true,
    "مهلت انجام": true,
    "پیشرفت": true,
    "عملیات": false,
  }

  return (
    <div className="w-full overflow-hidden rounded-xl border border-blue-gray-100 [&_button]:min-w-0">
      <table className="w-full min-w-[900px] table-fixed text-right">
        <colgroup>
          <col className="w-[5%]"/>
          {roleId !== 3 && <col className="w-[14%]"/>}
          {roleId !== 2 && <col className="w-[14%]"/>}
          <col className="w-[13%]"/>
          <col className="w-[8%]"/>
          <col className="w-[18%]"/>
          <col className="w-[13%]"/>
          <col className="w-[15%]"/>
          <col className="w-[8%]"/>
        </colgroup>
        <TableHead
          tableHead={TABLE_HEAD}
          headSortable={headSortable}
          headMap={headMap}
          sortType={sortType}
          setSortType={setSortType}
          setSortBy={setSortBy}
          selectedHead={selectedHead}
          setSelectedHead={setSelectedHead}
        />
        <tbody className="textCenter divide-y divide-blue-gray-50">
        {serviceFollowUps.length === 0 && (
          <tr>
            <td colSpan={TABLE_HEAD.length} className="py-12 text-center">
              <Typography color="blue-gray" className="font-normal">
                موردی یافت نشد
              </Typography>
            </td>
          </tr>
        )}
        {serviceFollowUps.map(
          ({
             id,
             person_name,
             agent_name,
             branch_name,
             step,
             step_title,
             dead_line,
             days_to_dead_line,
             progress
          }, index) => {
            const classes = "p-2";
            const deadlineStatus = getDeadlineStatus(days_to_dead_line);
            const progressValue = Number(progress) || 0;

            return (
              <tr
                key={id}
                className={`transition-colors ${deadlineStatus.rowClassName}`}>

                <td className={classes}>
                  <div className="flex justify-center">
                    <Typography color="blue-gray">
                      {persianNumberFormat(index + 1, true)}
                    </Typography>
                  </div>
                </td>

                {roleId !== 3 &&
                  <td className={classes}>
                    <div className="flex justify-center px-2">
                      <Typography color="blue-gray" className="textRight break-words">
                        {person_name || "-"}
                      </Typography>
                    </div>
                  </td>
                }

                {roleId !== 2 &&
                  <td className={classes}>
                    <div className="flex justify-center px-2">
                      <Typography color="blue-gray" className="textRight break-words">
                        {agent_name || "-"}
                      </Typography>
                    </div>
                  </td>
                }

                <td className={classes}>
                  <div className="flex justify-center px-2">
                    <Typography color="blue-gray" className="textRight break-words">
                      {branch_name || "-"}
                    </Typography>
                  </div>
                </td>

                <td className={classes}>
                  <div className="flex justify-center px-2">
                    <Typography color="blue-gray" className="textRight">
                      {persianNumberFormat(step)}
                    </Typography>
                  </div>
                </td>

                <td className={classes}>
                  <div className="flex justify-center px-2">
                    <Typography color="blue-gray" className="textRight break-words">
                      {step_title || "-"}
                    </Typography>
                  </div>
                </td>

                <td className={classes}>
                  <div className="flex flex-col items-center justify-center gap-1 px-2">
                    <Typography color="blue-gray">
                      {formatDate(dead_line)}
                    </Typography>
                    <Chip
                      variant="ghost"
                      value={deadlineStatus.label}
                      color={deadlineStatus.chipColor}
                      className="inline-flex justify-center text-xs"
                    />
                  </div>
                </td>

                <td className={classes}>
                  <div className="flex min-w-32 flex-col justify-center px-2">
                    <Typography color="blue-gray">
                      {`${persianNumberFormat(progressValue)} %`}
                    </Typography>
                    <div className="flex justify-center px-2">
                      <Progress value={progressValue} size="sm"
                                className="text-right"/>
                    </div>
                  </div>
                </td>

                <td className={classes}>
                  <Tooltip content="ویرایش">
                    <IconButton variant="text" className="text-align=right" onClick={() => {
                        setServiceFollowUp(serviceFollowUps[index]);
                        setOpen(true);
                      }}>
                      <PencilIcon className="h-4 w-4"/>
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            );
          },
        )}
        </tbody>
      </table>
    </div>
  );
}
